/**
 * Form controls, in one file so every box on every screen behaves the same way.
 *
 * Three properties are not negotiable here and each has a reason on a phone:
 *
 *   Every control is at least 44px tall and every label is a real `<label for>`, because this is
 *   typed one-handed and a mistyped ZIP sends somebody to the wrong door.
 *
 *   Every control is 16px, set in styles.css, because anything smaller makes iOS zoom the page on
 *   focus and you lose your place halfway down a form this long.
 *
 *   A required field says so in words. An asterisk is a convention, not an instruction.
 *
 * AT A DESK THE SAME CONTROLS ARE A DESK'S SIZE (ops #56). The `desk:` variant in styles.css is a
 * laptop's width with a mouse, and there a box and a button are about 36px tall with 14px type,
 * because a thumb's 44px on a 1440 screen is what put two enquiries where ten belong. Phones and
 * tablets never match it, so every target under a finger keeps the 44px it had.
 */
import type { ReactNode } from 'react';
import { MEASURE } from './AppShell.tsx';

/** The least height of anything pressed: 44px under a thumb, 36px under a pointer at a desk. */
export const TARGET_SIZE = 'min-h-11 desk:min-h-9';

/** A box's height and padding: a thumb's on a phone and a tablet, a pointer's at a desk. */
export const BOX_SIZE = 'min-h-11 px-3 py-2 desk:min-h-9 desk:py-1.5';

/** A button's height and padding, 48px under a thumb and 36px at a desk. */
export const BUTTON_SIZE = 'min-h-12 px-5 desk:min-h-9 desk:px-4';

function Label({
  htmlFor,
  label,
  required,
}: {
  htmlFor: string;
  label: string;
  required?: boolean;
}) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium">
      {label}
      {required ? <span className="ml-1 text-xs font-normal text-navy/65">required</span> : null}
    </label>
  );
}

const BOX = `mt-1 block w-full rounded-lg border border-navy/25 bg-parchment text-navy ${BOX_SIZE}`;

export function Field({
  id,
  label,
  hint,
  required,
  children,
}: {
  id: string;
  label: string;
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="mb-4">
      <Label htmlFor={id} label={label} required={required} />
      {children}
      {hint ? (
        <p id={`${id}-hint`} className={`mt-1 text-xs text-navy/65 ${MEASURE}`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function TextField({
  id,
  label,
  value,
  onChange,
  hint,
  required,
  type = 'text',
  inputMode,
  autoComplete,
  placeholder,
  list,
  max,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  required?: boolean;
  type?: 'text' | 'tel' | 'email' | 'date' | 'time';
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
  autoComplete?: string;
  placeholder?: string;
  /**
   * The latest day a date box will take, which is what keeps a browser's own calendar from
   * offering one the screen is about to refuse. It is never the control: the same rule is checked
   * again before anything is sent, because a typed day reaches the box without the calendar.
   */
  max?: string;
  /**
   * The id of a `<datalist>` the caller draws. It offers what is already stored without refusing
   * anything else, which is what a source detail needs: the fourth spelling of one Nextdoor thread
   * is three rows too many in a roll-up, and a box that only takes the old answers is a box people
   * type past into the notes.
   */
  list?: string;
}) {
  return (
    <Field id={id} label={label} hint={hint} required={required}>
      <input
        id={id}
        type={type}
        value={value}
        inputMode={inputMode}
        autoComplete={autoComplete}
        placeholder={placeholder}
        list={list}
        max={max}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={BOX}
      />
    </Field>
  );
}

export function TextAreaField({
  id,
  label,
  value,
  onChange,
  hint,
  rows = 3,
  emphasis,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  rows?: number;
  /** Draws the coral edge. Used for the do-not list, and for nothing that matters less. */
  emphasis?: boolean;
}) {
  return (
    <Field id={id} label={label} hint={hint}>
      <textarea
        id={id}
        rows={rows}
        value={value}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={`${BOX} ${emphasis ? 'border-l-4 border-l-coral' : ''}`}
      />
    </Field>
  );
}

/**
 * A select over a value list from `@fk/shared`. The options are ALWAYS passed in from there and
 * never retyped: one definition, two consumers, and a value this interface invents cannot exist.
 * The one cast in this file is here, at the single point where the DOM hands back a bare string.
 */
export function SelectField<T extends string>({
  id,
  label,
  value,
  onChange,
  options,
  toLabel,
  placeholder,
  hint,
  required,
}: {
  id: string;
  label: string;
  value: T | '';
  onChange: (value: T) => void;
  options: readonly T[];
  toLabel: (value: T) => string;
  /** When given, the field starts empty and the person has to choose. */
  placeholder?: string;
  hint?: ReactNode;
  required?: boolean;
}) {
  return (
    <Field id={id} label={label} hint={hint} required={required}>
      <select
        id={id}
        value={value}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(event) => onChange(event.target.value as T)}
        className={BOX}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option} value={option}>
            {toLabel(option)}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function Button({
  children,
  onClick,
  type = 'button',
  tone = 'primary',
  disabled,
  full,
  describedBy,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  tone?: 'primary' | 'quiet';
  disabled?: boolean;
  full?: boolean;
  /** The id of the sentence that says why it is disabled, read with it by a screen reader. */
  describedBy?: string;
}) {
  const tones = {
    primary: 'bg-navy text-cream border-navy',
    quiet: 'bg-cream text-navy border-navy/25',
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-describedby={describedBy}
      className={`inline-flex items-center justify-center rounded-lg border text-base font-medium disabled:opacity-50 ${BUTTON_SIZE} ${tones[tone]} ${full ? 'w-full' : ''}`}
    >
      {children}
    </button>
  );
}
